import { useState } from 'react'
import { useSuppliers, useDeleteSupplier } from '@hooks/useProcurement'
import { SupplierForm } from '@components/procurement/SupplierForm'

export function SupplierListPage() {
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useSuppliers(search)
  const delete_ = useDeleteSupplier()

  const suppliers = data?.data?.items || []
  const total = data?.data?.total || 0
  const pageSize = data?.data?.pageSize || 20
  const totalPages = data?.data?.totalPages || 0

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      delete_.mutate(id)
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingId(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suppliers</h1>
          <p className="text-gray-600 text-sm mt-1">Manage your supplier database</p>
        </div>
        <button
          onClick={() => {
            setEditingId(null)
            setShowForm(!showForm)
          }}
          className="btn-primary"
        >
          + Add Supplier
        </button>
      </div>

      {showForm && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              {editingId ? 'Edit Supplier' : 'New Supplier'}
            </h2>
          </div>
          <SupplierForm
            initialData={
              editingId
                ? suppliers.find((s) => s.id === editingId)
                : null
            }
            onSuccess={handleFormClose}
            onCancel={handleFormClose}
          />
        </div>
      )}

      <div className="card">
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            placeholder="Search by name or GSTIN..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="flex-1"
          />
        </div>

        {error && (
          <div className="alert alert-error">
            Failed to load suppliers. Please try again.
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No suppliers found matching your search.' : 'No suppliers yet. Create one to get started.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Name
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      GSTIN
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Phone
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Address
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map((supplier) => (
                    <tr
                      key={supplier.id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-3 font-medium">{supplier.name}</td>
                      <td className="px-4 py-3 text-sm">{supplier.gstin || '-'}</td>
                      <td className="px-4 py-3 text-sm">{supplier.phone || '-'}</td>
                      <td className="px-4 py-3 text-sm truncate max-w-xs">
                        {supplier.address || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingId(supplier.id)
                              setShowForm(true)
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(supplier.id)}
                            disabled={delete_.isPending}
                            className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing {(page - 1) * pageSize + 1} to{' '}
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
